import { searchParamsData } from './sbAPI'
import { getStoryblokApi } from './storyblok-api'

interface CachedOffice {
  id: string
  name: string
  full_slug: string
  postcode?: string
  suburb?: string
  city?: string
  content: {
    component: string
    office?: Array<{
      address?: string
      location?: Array<{
        latitude: string
        longitude: string
      }>
    }>
    postcode?: string
    suburb?: string
    city?: string
    Latitude?: string
    Longitude?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface OfficeCache {
  offices: CachedOffice[]
  timestamp: number
  location: string
}

export class OfficeDataCache {
  private static CACHE_KEY = 'findex-offices-cache'
  private static CACHE_DURATION = 60 * 60 * 1000 // 1 hour
  private static isLoading = false
  private static loadingPromise: Promise<CachedOffice[]> | null = null

  private static getFromCache(location: string): CachedOffice[] | null {
    if (typeof window === 'undefined') return null

    try {
      const stored = localStorage.getItem(this.CACHE_KEY)
      if (stored == null || stored === '') return null

      const cache: OfficeCache = JSON.parse(stored)

      // Check if cache is valid (not expired and same location)
      if (cache.location === location && Date.now() - cache.timestamp < this.CACHE_DURATION) {
        return cache.offices
      }

      return null
    } catch (error) {
      console.warn('Failed to load office cache:', error)
      return null
    }
  }

  private static saveToCache(offices: CachedOffice[], location: string) {
    if (typeof window === 'undefined') return

    try {
      const cache: OfficeCache = {
        offices,
        timestamp: Date.now(),
        location,
      }
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache))
    } catch (error) {
      console.warn('Failed to save office cache:', error)
    }
  }

  private static async fetchAllOfficesFromStoryblok(
    location: string,
    filterLocation: string
  ): Promise<CachedOffice[]> {
    const allOffices: CachedOffice[] = []
    let currentPage = 1
    let hasMorePages = true

    try {
      while (hasMorePages) {
        const response = await getStoryblokApi().get('cdn/stories', {
          ...searchParamsData,
          filter_query: {
            component: { in: 'advanceOffice,office' },
            __or: [
              { 'settings.location_available': { in: location } },
              { 'settings.location_available': { not_in: filterLocation } },
            ],
          },
          per_page: 100,
          page: currentPage,
        })

        const data = response.data
        const headers = Object.fromEntries(Object.entries(response.headers))

        if (data?.stories != null) {
          allOffices.push(...data.stories)
        }

        const total = Number(headers.total ?? '0')
        const currentCount = currentPage * 100
        hasMorePages = currentCount < total
        currentPage++

        // Safety limit to prevent infinite loops
        if (currentPage > 20) {
          console.warn('Hit page limit safety check')
          break
        }
      }

      return allOffices
    } catch (error) {
      console.error('Error fetching offices from Storyblok:', error)
      return []
    }
  }

  static async getAllOffices(location: string, filterLocation: string): Promise<CachedOffice[]> {
    // Check cache first
    const cached = this.getFromCache(location)
    if (cached) return cached

    // If already loading, return the same promise
    if (this.isLoading && this.loadingPromise) {
      return this.loadingPromise
    }

    // Start loading
    this.isLoading = true
    this.loadingPromise = this.fetchAllOfficesFromStoryblok(location, filterLocation)
      .then((offices) => {
        this.saveToCache(offices, location)
        return offices
      })
      .finally(() => {
        this.isLoading = false
        this.loadingPromise = null
      })

    return this.loadingPromise
  }

  static isCurrentlyLoading(): boolean {
    return this.isLoading
  }

  static clearCache() {
    if (typeof window === 'undefined') return
    localStorage.removeItem(this.CACHE_KEY)
  }
}
